export const TARGET_GRID_SQUARES = 5;
export const TARGET_GRID_SQUARES_2D = 10;
export const TARGET_GRID_LINES_2D = 12;
export const LEFT_SHIFTED_NEGATIVE_CELLS = 2;

export type TAxisRange = {
  min: number;
  max: number;
  interval: number;
};

export function niceGridInterval(span: number, targetSquares = TARGET_GRID_SQUARES): number {
  if (!(span > 0) || !Number.isFinite(span)) {
    return 1;
  }

  const raw = span / targetSquares;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const residual = raw / magnitude;

  if (residual <= 1) {
    return magnitude;
  }

  if (residual <= 2) {
    return 2 * magnitude;
  }

  if (residual <= 5) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
}

export function ceilToInterval(value: number, interval: number): number {
  return Math.ceil(value / interval) * interval;
}

export function niceFloorInterval(maxInterval: number): number {
  if (!(maxInterval > 0) || !Number.isFinite(maxInterval)) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(maxInterval));
  const residual = maxInterval / magnitude;

  if (residual >= 5) {
    return 5 * magnitude;
  }

  if (residual >= 2) {
    return 2 * magnitude;
  }

  if (residual >= 1) {
    return magnitude;
  }

  return magnitude / 10;
}

/**
 * Expand a range around its center to an exact `span` that is a multiple of `interval`
 * (complete grid squares), still covering `[minValue, maxValue]`.
 */
export function expandRangeToSpan(
  minValue: number,
  maxValue: number,
  span: number,
  interval: number,
): { min: number; max: number } {
  const center = (minValue + maxValue) / 2;
  let min = Math.floor((center - span / 2) / interval) * interval;
  let max = min + span;

  if (minValue < min) {
    min = Math.floor(minValue / interval) * interval;
    max = min + span;
  }

  if (maxValue > max) {
    max = Math.ceil(maxValue / interval) * interval;
    min = max - span;
  }

  return { min, max };
}

function readExtent(values: number[]): { min: number; max: number } {
  if (!values.length) {
    return { min: 0, max: 1 };
  }

  let min = values[0];
  let max = values[0];

  for (const value of values) {
    if (value < min) {
      min = value;
    }

    if (value > max) {
      max = value;
    }
  }

  return { min, max };
}

function maxAbs(values: number[]): number {
  let result = 0;

  for (const value of values) {
    const abs = Math.abs(value);

    if (abs > result) {
      result = abs;
    }
  }

  return result;
}

function completeHalfSpan(halfRaw: number): { half: number; interval: number } {
  const span = Math.max(2 * halfRaw, 1);
  const interval = niceGridInterval(span, TARGET_GRID_SQUARES_2D);
  const half = Math.max(ceilToInterval(halfRaw, interval), interval);

  return { half, interval };
}

/** One extra grid cell above 0 when the inverted axis top is 0. */
export function padInvertedZeroTop(range: TAxisRange): TAxisRange {
  if (Math.abs(range.min) > 1e-9) {
    return range;
  }

  return {
    ...range,
    min: range.min - range.interval,
  };
}

/** Horizontal 2D axis: 0 in the middle, negatives left, positives right. */
export function computeCenteredCartesianRange(values: number[]): TAxisRange {
  const { half, interval } = completeHalfSpan(Math.max(maxAbs(values), 1));

  return {
    min: -half,
    max: half,
    interval,
  };
}

/** Horizontal 2D axis: 0 shifted left, with a fixed number of negative cells. */
export function computeLeftShiftedCartesianRange(values: number[]): TAxisRange {
  const { min: minData, max: maxData } = readExtent(values);
  const positiveMax = Math.max(maxData, 0, 1);
  const positiveCells = Math.max(TARGET_GRID_LINES_2D - LEFT_SHIFTED_NEGATIVE_CELLS, 1);
  const interval = niceFloorInterval(positiveMax / positiveCells);
  let min = -LEFT_SHIFTED_NEGATIVE_CELLS * interval;

  if (minData < min) {
    min = Math.floor(minData / interval) * interval;
  }

  const max = Math.max(ceilToInterval(Math.max(maxData, 0), interval), interval);

  return { min, max, interval };
}

/** Vertical 2D axis: fit actual values, round span up to complete grid cells. */
export function computeFittedCellRange(values: number[]): TAxisRange {
  const { min: minData, max: maxData } = readExtent(values);
  let span = Math.max(maxData - minData, 1);
  const interval = niceGridInterval(span, TARGET_GRID_SQUARES_2D);
  span = Math.max(ceilToInterval(span, interval), interval);

  return {
    ...expandRangeToSpan(minData, maxData, span, interval),
    interval,
  };
}

/** NS×WE: both axes Cartesian, 0 in the middle, equal spans. */
export function computePlanSquareRanges(xValues: number[], yValues: number[]): { x: TAxisRange; y: TAxisRange } {
  const { half, interval } = completeHalfSpan(Math.max(maxAbs(xValues), maxAbs(yValues), 1));
  const range: TAxisRange = { min: -half, max: half, interval };

  return { x: range, y: range };
}

/**
 * NS×WE: 0 in the middle. Each axis uses its own data max; a shared interval
 * rounds both to complete cells — a square when maxima match, otherwise a rectangle.
 */
export function computePlanCartesianRanges(
  xValues: number[],
  yValues: number[],
  minHalf = 1,
): { x: TAxisRange; y: TAxisRange } {
  const xHalfRaw = Math.max(maxAbs(xValues), minHalf);
  const yHalfRaw = Math.max(maxAbs(yValues), minHalf);
  const interval = niceGridInterval(Math.max(2 * xHalfRaw, 2 * yHalfRaw, 1), TARGET_GRID_SQUARES_2D);
  const xHalf = Math.max(ceilToInterval(xHalfRaw, interval), interval);
  const yHalf = Math.max(ceilToInterval(yHalfRaw, interval), interval);

  return {
    x: { min: -xHalf, max: xHalf, interval },
    y: { min: -yHalf, max: yHalf, interval },
  };
}
